import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/api';

interface HelpSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: 'worker' | 'management' | 'superuser';
  onOpenReachOut?: () => void;
}

type AudienceTag = 'all' | 'worker' | 'management';
type PriorityTag = 'high' | 'normal';

interface HelpSection {
  id: string;
  title: string;
  audience: AudienceTag;
  priority: PriorityTag;
  content: React.ReactNode;
}

const extractTextFromReactNode = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(' ');
  if (React.isValidElement(node) && node.props.children) return extractTextFromReactNode(node.props.children);
  return '';
};

const SectionCard: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div className="bg-vineyard-light border border-wood-200 rounded p-3">
    <h5 className="font-medium text-wood-900 mb-2">{title}</h5>
    <ol className="text-sm text-wood-700 space-y-1 list-decimal list-inside">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  </div>
);

const ReportProblemForm: React.FC = () => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/activity/report-error', {
        message: message.trim(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        severity: 'user_report',
      });
      setSent(true);
      setMessage('');
    } catch {
      setSent(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-wood-600">Describe the issue or suggestion. We review these to improve usability.</p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What happened, where it happened, and what you expected."
          className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 min-h-[88px]"
          maxLength={2000}
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={submitting || !message.trim()}
          className="text-sm font-medium text-grape-600 hover:text-grape-700 disabled:opacity-50"
        >
          {submitting ? 'Sending...' : 'Send report'}
        </button>
      </form>
      {sent && <p className="text-sm text-leaf-600">Thanks, your report was sent.</p>}
    </div>
  );
};

const HelpSidebar: React.FC<HelpSidebarProps> = ({ isOpen, onClose, userRole, onOpenReachOut }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['quick-start']));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('quick-start');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const helpSections: HelpSection[] = useMemo(
    () => [
      {
        id: 'quick-start',
        title: 'Quick Start By Role',
        audience: 'all',
        priority: 'high',
        content: (
          <div className="space-y-4 pt-3">
            <div className="bg-grape-50 border border-grape-200 rounded p-3">
              <p className="text-sm text-wood-700">
                This menu is a practical guide. Open a section, follow the steps, and match each page name with the left
                navigation.
              </p>
            </div>
            {userRole === 'worker' ? (
              <SectionCard
                title="Worker first-shift checklist"
                items={[
                  'Open Worker Dashboard and clock in.',
                  'Start assigned tasks first, then write useful notes while working.',
                  'End each task immediately when finished so hours stay accurate.',
                  'Review entries in Worker Reporting before you clock out.',
                ]}
              />
            ) : (
              <SectionCard
                title="Manager first-day checklist"
                items={[
                  'Open Crew Control to confirm who is active and available.',
                  'Use Manager Tasks to assign work with clear instructions and timing.',
                  'Review block, inventory, and time status before day-end approvals.',
                  'Keep setup pages updated so workers see clean options and fewer mistakes.',
                ]}
              />
            )}
          </div>
        ),
      },
      {
        id: 'manager-command-center',
        title: 'Manager Command Center',
        audience: 'management',
        priority: 'high',
        content: (
          <div className="space-y-3 pt-3">
            <SectionCard
              title="Crew Control (`/manager-dashboard`)"
              items={[
                'Check who is clocked in and what they are currently doing.',
                'Watch for blocked work and rebalance tasks before delays grow.',
                'Use this page at day start, mid-day, and before close-out.',
              ]}
            />
            <SectionCard
              title="Manager Tasks (`/manager-tasks`)"
              items={[
                'Create tasks with worker, block, and exact expected outcome.',
                'Set priority and due timing so workers can sequence correctly.',
                'Track completion notes and quickly reassign if work is blocked.',
              ]}
            />
            <div className="bg-wood-50 border border-wood-200 rounded p-3">
              <p className="text-sm text-wood-700">
                Usability suggestion: keep task naming consistent with worker-facing labels to reduce rework and questions.
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'worker-day-workflow',
        title: 'Worker Day Workflow',
        audience: 'worker',
        priority: 'high',
        content: (
          <div className="space-y-3 pt-3">
            <SectionCard
              title="Worker Dashboard (`/worker-dashboard`)"
              items={[
                'Clock in before starting any task.',
                'Start only the task you are actively working on.',
                'Add clear notes: block, actions completed, and issues found.',
                'End tasks immediately after completion and move to next task.',
                'Clock out only after active tasks are closed.',
              ]}
            />
            <SectionCard
              title="Worker Reporting (`/worker-reporting`)"
              items={[
                'Review hours and completed tasks daily.',
                'Flag anything that looks incorrect before payroll approval.',
                'Use notes to explain unusually long work periods.',
              ]}
            />
          </div>
        ),
      },
      {
        id: 'setup-reference',
        title: 'Setup and Reference',
        audience: 'management',
        priority: 'normal',
        content: (
          <div className="space-y-3 pt-3">
            <SectionCard
              title="Core setup pages"
              items={[
                'Vineyard Info (`/vineyard`): organization details and defaults.',
                'Blocks (`/blocks`): add and maintain unit records before assignment.',
                'User Management (`/user-management`): invite users and set access.',
                'Equipment (`/equipment`): keep equipment records and status current.',
                'Reference Data (`/reference-data`): maintain controlled lists and options.',
              ]}
            />
            <p className="text-sm text-wood-600">
              Keeping setup pages clean improves data quality across inventory, tasks, reporting, and approvals.
            </p>
          </div>
        ),
      },
      {
        id: 'production-how-to',
        title: 'Production Pages How-To',
        audience: 'management',
        priority: 'normal',
        content: (
          <div className="space-y-3 pt-3">
            <SectionCard
              title="Daily operations pages"
              items={[
                'Inventory (`/inventory`): receive, adjust, count, and review stock movement.',
                'Inputs (`/inputs`): record products, rates, labor, and equipment used.',
                'Scouting (`/scouting`): log findings and follow-up actions.',
                'Disease Pressure (`/disease-pressure`): review trends and treatment guidance.',
                'Maintenance (`/maintenance`): track equipment work and completion status.',
              ]}
            />
            <SectionCard
              title="Monitoring and planning pages"
              items={[
                'Dashboard (`/dashboard`) for top-level operational summary.',
                'Sensors (`/sensors`) and Sensor Configuration (`/sensors/config`).',
                'Machinery (`/machinery`) for live usage and schedule context.',
                'Harvests (`/harvests`) for quantity and quality records.',
                'Monitoring (`/ai/monitoring`) and AI Insights (`/ai/insights`) for automation support.',
              ]}
            />
          </div>
        ),
      },
      {
        id: 'quality-business',
        title: 'Quality, Compliance, and Business',
        audience: 'management',
        priority: 'normal',
        content: (
          <div className="space-y-3 pt-3">
            <SectionCard
              title="Quality and compliance pages"
              items={[
                'Completed Quality Gates (`/completed-quality-gates`) for audit-ready checks.',
                'Contracts (`/contracts`) and Permits (`/permits`) for legal operations.',
                'Wine Tasting (`/wine-tasting`) for sensory results and quality history.',
              ]}
            />
            <SectionCard
              title="Decision support pages"
              items={[
                'Dictionary (`/dictionary`) for consistent terms.',
                'Calculations (`/calculations`) for standard formulas.',
                'Time Export (`/time-export`) for payroll/report handoff.',
                'Subscription (`/subscription`) for owner billing and plan changes.',
              ]}
            />
          </div>
        ),
      },
      {
        id: 'about-support',
        title: 'Support and Feedback',
        audience: 'all',
        priority: 'normal',
        content: (
          <div className="space-y-4 pt-3">
            <div className="bg-wood-50 border border-wood-200 rounded p-3">
              <h5 className="font-medium text-wood-900 mb-2">Get help quickly</h5>
              <ul className="text-sm text-wood-700 space-y-1 list-disc list-inside">
                <li>Use search to jump to a workflow.</li>
                <li>Use Reach Out for direct support.</li>
                <li>Use the report form below for bugs and usability ideas.</li>
              </ul>
            </div>
            <div className="bg-vineyard-light border border-wood-200 rounded p-3">
              <h5 className="font-medium text-wood-900 mb-2">Report a problem</h5>
              <ReportProblemForm />
            </div>
          </div>
        ),
      },
    ],
    [userRole]
  );

  const visibleSections = useMemo(() => {
    const roleFiltered = helpSections.filter((section) => {
      if (userRole === 'worker') {
        return section.audience === 'worker' || section.audience === 'all';
      }
      return true;
    });

    const sorted = [...roleFiltered].sort((a, b) => {
      const rank = (section: HelpSection): number => {
        if (userRole !== 'worker' && section.audience === 'management') return 0;
        if (section.priority === 'high') return 1;
        return 2;
      };
      return rank(a) - rank(b);
    });

    if (!searchQuery.trim()) return sorted;

    const queryLower = searchQuery.toLowerCase();
    return sorted.filter((section) => {
      if (section.title.toLowerCase().includes(queryLower)) return true;
      return extractTextFromReactNode(section.content).toLowerCase().includes(queryLower);
    });
  }, [helpSections, searchQuery, userRole]);

  useEffect(() => {
    if (visibleSections.length === 0) return;
    const firstId = visibleSections[0].id;
    setExpandedSections(new Set([firstId]));
    setActiveSection(firstId);
  }, [userRole, searchQuery, visibleSections]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
    setActiveSection(sectionId);
  }, []);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth < 768;
    return false;
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timeoutId = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen]);

  return (
    <>
      {isOpen && isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 z-50" onClick={onClose} />}

      <div
        className={`
        fixed bg-vineyard-white shadow-xl transition-transform duration-300 ease-out z-50 lg:z-40
        ${isMobile ? 'bottom-0 left-0 right-0 h-[85vh] rounded-t-2xl' : 'right-0 top-0 h-full w-full max-w-md lg:shadow-lg'}
        ${isOpen ? (isMobile ? 'translate-y-0' : 'translate-x-0') : isMobile ? 'translate-y-full' : 'translate-x-full'}
        ${!isOpen ? 'pointer-events-none' : ''}
      `}
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-wood-200 px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-grape-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-grape-600 text-lg">🍇</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-wood-900">How To Use The App</h2>
                  <p className="text-xs text-wood-500">
                    {userRole === 'worker' ? 'Worker workflows only' : 'Manager workflows first, all topics included'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-metal-400 hover:text-metal-600 p-1 rounded-full hover:bg-metal-100 transition-colors touch-manipulation"
                aria-label="Close help"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isMobile && (
              <div className="flex justify-center mb-3">
                <div className="w-12 h-1 bg-wood-300 rounded-full" />
              </div>
            )}

            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search help topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-4 py-2 text-sm border-2 border-wood-300 rounded focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                aria-label="Search help topics"
              />
            </div>

            {onOpenReachOut && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => {
                    onOpenReachOut();
                    onClose();
                  }}
                  className="w-full text-sm font-medium text-grape-600 hover:text-grape-700 hover:bg-grape-50 py-2 px-3 rounded border border-grape-200 touch-manipulation"
                >
                  Reach Out
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {searchQuery && (
              <div className="mb-4 p-3 bg-wood-50 border-2 border-wood-200 rounded">
                <span className="text-sm text-wood-800">
                  {visibleSections.length} result{visibleSections.length !== 1 ? 's' : ''} found for "{searchQuery}"
                </span>
              </div>
            )}

            <div className="space-y-3">
              {visibleSections.map((section) => {
                const isExpanded = expandedSections.has(section.id);
                const isActive = activeSection === section.id;
                const isHighPriority = section.priority === 'high';

                return (
                  <div
                    key={section.id}
                    className={`border rounded transition-all duration-200 ${
                      isActive ? 'border-grape-300 bg-grape-50' : 'border-wood-200 hover:border-wood-300'
                    }`}
                  >
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex w-full items-center justify-between p-4 text-left hover:bg-vineyard-light transition-colors rounded"
                      aria-expanded={isExpanded}
                      aria-controls={`help-section-${section.id}`}
                    >
                      <div className="flex items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${isHighPriority ? 'bg-orange-100' : 'bg-grape-100'}`} />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="font-medium text-wood-900">{section.title}</span>
                            {isHighPriority && (
                              <span className="ml-2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">Essential</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isExpanded && <div className="w-2 h-2 bg-grape-500 rounded-full" />}
                        <span className="text-metal-400">{isExpanded ? '▼' : '▶'}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div id={`help-section-${section.id}`} className="px-4 pb-4 border-t border-wood-200 bg-white" role="region">
                        {section.content}
                      </div>
                    )}
                  </div>
                );
              })}

              {visibleSections.length === 0 && searchQuery && (
                <div className="text-center py-8">
                  <h3 className="text-lg font-medium text-wood-900 mb-2">No results found</h3>
                  <p className="text-sm text-wood-600 mb-4">Try different keywords, or clear search and browse all topics.</p>
                  <button onClick={() => setSearchQuery('')} className="text-grape-600 hover:text-grape-700 text-sm font-medium">
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpSidebar;
